<template>
  <div>
    <OUpload ref="upload" multiple drag-drop aria-label="Upload">
      <section class="ex-center">
        <p>
          <NrIcon class="upload-icon" name="upload" iconType="solid" />
        </p>
        <p class="p-bottom-32">
          Drop your files and folders here or click to upload (files only)
        </p>
      </section>
    </OUpload>
    <NrMessage v-if="uploadLimitError" class="m-top-16">
      Too many files selected for upload. Select {{ uploadLimit }} or less
      files.
    </NrMessage>
    <div v-for="(file, index) in filesReadyForUpload" :key="index">
      <div class="m-bottom-8">
        <div class="p-bottom-4">
          <span>
            {{ file.name }}
          </span>
        </div>
        <NrProgress
          :id="file.name + file.lastModified"
          :value="0"
          show-value
          format="percent"
          >Queued</NrProgress
        >
      </div>
    </div>
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty'
import uniqBy from 'lodash/uniqBy'
import NrIcon from '@nextrequestco/components/src/components/NrIcon/NrIcon.vue'
import { v4 as uuidv4 } from 'uuid'
import OUpload from '@oruga-ui/oruga-next/src/components/upload/Upload.vue'
import NrMessage from '@nextrequestco/components/src/components/NrMessage/nr-message'
import NrProgress from '@nextrequestco/components/src/components/NrProgress/nr-progress'

export default {
  components: { NrIcon, OUpload, NrMessage, NrProgress },
  props: {
    uploadLimit: {
      type: Number,
      required: true
    },
    account: {
      type: Object
    },
    evaporateResponse: {
      type: Object,
      default() {
        return {}
      }
    },
    s3Bucket: {
      type: String
    },
    requestId: {
      type: String
    }
  },

  mounted() {
    // To support dropping folders, which the `OUpload` component does not, this binds
    // our own event handlers to the component.
    this.$refs.upload.$el.addEventListener(
      'drop',
      this.onFilesDropped.bind(this)
    )
    this.$refs.upload.$el.addEventListener(
      'change',
      this.onFilesSelected.bind(this)
    )
  },

  data() {
    return {
      filesReadyForUpload: [], // List of files that are currently uploading or in the queued list
      uploadLimitError: false
    }
  },

  computed: {
    uploadLimitErrorMessage() {
      return `Too many files selected for upload. Select ${this.uploadLimit} or less files.`
    }
  },

  methods: {
    getFile(entry) {
      return new Promise((resolve, reject) => entry.file(resolve, reject))
    },

    getEntry(dataTransferItem) {
      return dataTransferItem.webkitGetAsEntry
        ? dataTransferItem.webkitGetAsEntry()
        : dataTransferItem.getAsEntry()
    },

    // Returns a Promise which resolves with an array of all the files from the given directory, including in nested directories.
    filesFromDirectory(directoryEntry) {
      const directoryReader = directoryEntry.createReader()
      return new Promise((resolve) => {
        directoryReader.readEntries(async (entries) => {
          const fileEntries = []
          for (const entry of entries) {
            if (entry.isDirectory) {
              fileEntries.push(...(await this.filesFromDirectory(entry)))
            } else {
              fileEntries.push(await this.getFile(entry))
            }
          }
          resolve(fileEntries)
        })
      })
    },

    async onFilesDropped(event) {
      const items = event.dataTransfer.items
      if (!items.length) return

      const promises = Array.from(items)
        .flatMap(this.getEntry)
        .map((entry) => {
          if (entry.isDirectory) {
            return this.filesFromDirectory(entry)
          } else {
            return this.getFile(entry)
          }
        })

      const files = await Promise.all(promises)
      this.beginUploadingFiles(files.flat())
    },

    onFilesSelected(event) {
      this.beginUploadingFiles(event.target.files)
    },

    uploadFiles(files) {
      if (this.uploadLimitReached(files)) {
        this.enableLimitErrorMsg(true)
        return
      } else if (!isEmpty(files)) {
        this.$emit('on-file-drop')
        this.enableLimitErrorMsg(false)
        this.filesReadyForUpload.push(...files)
      }

      if (!this.s3Bucket || this.s3Bucket === '') {
        const message = 'S3 Bucket not set'
        window?.Sentry?.captureMessage(message)
        return
      }

      if (isEmpty(this.evaporateResponse) !== true) {
        let s3Folder
        if (this.requestId) {
          s3Folder = `${this.requestId}`
        } else {
          s3Folder = 'free_standing_or_uploaded_with_req_docs'
        }

        // Begin processing files
        if (files.length === 0) {
          return
        }

        const uploadPromises = files.flatMap((file) =>
          this.processFile(file, s3Folder)
        )

        Promise.all(uploadPromises)
          .then(() => {
            this.$emit('on-all-file-promises-resolved')
          })
          .catch((e) => {
            window?.Sentry?.captureException(e)
          })
      } else {
        const message = 'Evaporate file uploader not set.'
        window?.Sentry?.captureMessage(message)
      }
    },

    beginUploadingFiles(inputFiles) {
      const filesToUpload = this.selectFilesToUpload(inputFiles)
      this.uploadFiles(filesToUpload)
    },

    callBacks(file) {
      const self = this
      return {
        progress: (progressValue) => {
          self.updateProgressBar(file, 'progress', progressValue)
        },
        error: () => {
          self.updateProgressBar(file, 'error')
        },
        warn: () => {
          self.updateProgressBar(file, 'warn')
        },
        complete: (_xhr, awsKey) => {
          self.updateProgressBar(file, 'complete')
          const doc = self.setupDocumentObjectForPostgres(file, awsKey)
          this.$emit('on-file-upload-complete', doc)
        }
      }
    },

    enableLimitErrorMsg(val) {
      this.uploadLimitError = val
    },

    selectFilesToUpload(inputFiles) {
      const uniqueFiles = uniqBy(inputFiles, (file) => file.name)
      const validFiles = uniqueFiles.filter(
        (file) => file.size > 0 && file.name !== '.DS_Store'
      )

      // TODO: should we display an error on duplicate name?
      const alreadyUploadedNames = this.filesReadyForUpload.map((f) => f.name)
      const newFiles = validFiles.filter(
        (file) => !alreadyUploadedNames.includes(file.name)
      )

      return newFiles
    },

    processFile(file, s3Folder) {
      const fileType = file.name.split('.').pop()
      let name = ''
      if (isEmpty(this.account) === true) {
        return
      }
      if (fileType === 'pdf' && this.account.pdf_conversion_enabled) {
        name = `${
          this.account.subdomain
        }/${s3Folder}/original/${uuidv4()}.${fileType}`
      } else {
        name = `${this.account.subdomain}/${s3Folder}/${uuidv4()}.${fileType}`
      }
      const callbackMethods = this.callBacks(file)
      const promise = this.evaporateResponse
        .add({
          name,
          file,
          complete: callbackMethods.complete,
          progress: callbackMethods.progress,
          error: callbackMethods.error,
          warn: callbackMethods.warn
        })
        .catch((e) => {
          window?.Sentry?.captureException(e)
        })
      return promise
    },

    setupDocumentObjectForPostgres(file, awsKey) {
      const docUploadUrl = `//${this.s3Bucket}.s3.amazonaws.com/${awsKey}`
      const docPath = docUploadUrl
      const docDesc = '' // TODO: Setup custom document description input field?
      const docName = file.name
      const docTitle = '' || docName // TODO: Setup custom document title input field?
      const docDate = '' // TODO: Setup custom document date?
      const fileName = docName
      return {
        description: docDesc,
        url: docPath,
        title: docTitle,
        filename: fileName,
        doc_date: docDate,
        link: false
      }
    },

    uploadLimitReached(files) {
      return files.length > this.uploadLimit
    },

    updateProgressBar(file, callBack, progressValue = '') {
      // callBack: Must be either 'progress', 'error' or 'complete'
      const progressBarWrapper = document.getElementById(
        `${file.name + file.lastModified}`
      )
      if (progressBarWrapper && progressBarWrapper.children) {
        const progressBarElement = progressBarWrapper.children[0]
        const progressValueElement = progressBarWrapper.children[1]

        if (callBack === 'progress' && !isNaN(progressValue)) {
          // Updates the progressbar for the file in the UI
          progressBarElement.value = Math.round(progressValue * 100)
          progressValueElement.innerText = `${progressBarElement.value}%`
          progressBarElement.classList.add('is-progress')
        } else if (callBack === 'complete') {
          // Sets success color
          progressBarElement.classList.add('is-complete')
        } else if (callBack === 'error') {
          // Sets the error message on the progress bar
          progressBarElement.classList.add('is-error')
          progressValueElement.innerText = 'Error uploading document try again'
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/spacing.scss';
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

.o-upl {
  position: relative;
  display: inline-flex;
  width: 100%;
  &__draggable {
    cursor: pointer;
    width: 100%;
    padding: $spacing-4;
    border: $border-width-default dashed $color-gray-20;
    border-radius: $border-radius-default;
    &:hover {
      background-color: $color-gray-20;
      border: $border-width-default dashed $color-purple-110;
    }
    .ex-center {
      text-align: center;
      .upload-icon {
        font-size: $font-size-5;
        margin: $spacing-32 auto $spacing-32 auto;
      }
    }
  }
  input[type='file'] {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    z-index: -1;
  }
}
</style>
